import { useState, useEffect } from 'react';
import { Building2, Check, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { getPlanDisplayName, getModuleDisplayName } from '../utils/permissions';

interface Module {
  id: string;
  name: string;
  slug: string;
  description: string;
  features: string[];
}

export function ModulesMetiersTab() {
  const { permissions, userPlan, selectModule, clearModule, refresh } = usePermissions();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState<string | null>(null);

  const canAccessModules = permissions?.access_modules_metiers || false;

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const { data } = await supabase
        .from('modules_metiers')
        .select('*')
        .order('name');

      if (data) {
        setModules(data);
      }
    } catch (error) {
      console.error('Error loading modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleSlug: string) => {
    if (!canAccessModules) return;

    setActivating(moduleSlug);

    try {
      const currentActive = userPlan?.selected_metier_module === moduleSlug;

      if (currentActive) {
        await clearModule();
      } else {
        await selectModule(moduleSlug);
      }

      await refresh();
    } catch (error) {
      console.error('Error toggling module:', error);
      alert('Erreur lors de la modification du module');
    } finally {
      setActivating(null);
    }
  };

  const isModuleActive = (moduleSlug: string) => {
    return userPlan?.selected_metier_module === moduleSlug;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!canAccessModules) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Modules métiers non disponibles
          </h3>
          <p className="text-gray-600 mb-6">
            Les modules métiers sont disponibles à partir du Pack Métier.
            Passez à un pack supérieur pour activer un module adapté à votre secteur.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Pack actuel : <span className="font-semibold">{getPlanDisplayName(userPlan?.current_plan || null)}</span>
          </p>
          <a
            href="/tarifs"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
          >
            Voir les offres
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">1 module activable</span> - Choisissez le module qui correspond le mieux à votre activité.
              Vous pouvez changer de module à tout moment.
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {modules.map((module) => {
          const isActive = isModuleActive(module.slug);
          const isLoading = activating === module.slug;

          return (
            <div
              key={module.id}
              className={`bg-white rounded-lg shadow-sm p-6 border-2 transition-colors ${
                isActive
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${isActive ? 'bg-blue-600' : 'bg-gray-100'}`}>
                    <Building2 className={`h-6 w-6 ${isActive ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {module.name}
                    </h3>
                    {isActive && (
                      <span className="inline-flex items-center text-xs font-medium text-blue-600">
                        <Check className="h-3 w-3 mr-1" />
                        Module actif
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {module.description}
              </p>

              <ul className="space-y-2 mb-6">
                {module.features.map((feature: string, idx: number) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleToggleModule(module.slug)}
                disabled={isLoading}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  isActive
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader className="animate-spin" size={16} />
                    Chargement...
                  </span>
                ) : isActive ? (
                  'Désactiver'
                ) : (
                  'Activer ce module'
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
